import { useMemo, useRef, useState } from "react";

const CLOZE_RE = /\{\{c(\d+)::(.+?)(?:::(.*?))?\}\}/gs;

/**
 * Cloze editing with a live preview.
 *
 * Selecting text and pressing "Hide selection" wraps it as `{{cN::…}}`. The
 * preview shows one line per ordinal — exactly the cards Anki will generate —
 * because the single most common cloze mistake is not realising that `c1` and
 * `c2` produce two separate cards.
 */
export default function ClozeEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [warning, setWarning] = useState("");

  const ordinals = useMemo(() => {
    const found = new Set<number>();
    for (const match of value.matchAll(CLOZE_RE)) found.add(Number(match[1]));
    return [...found].sort((a, b) => a - b);
  }, [value]);

  const gaps = useMemo(() => {
    if (!ordinals.length) return [];
    const expected = Array.from({ length: ordinals[ordinals.length - 1] }, (_, i) => i + 1);
    return expected.filter((n) => !ordinals.includes(n));
  }, [ordinals]);

  const wrap = (ordinal: number) => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart, selectionEnd } = el;
    if (selectionStart === selectionEnd) {
      setWarning("Select the text you want to hide first.");
      return;
    }
    const selected = value.slice(selectionStart, selectionEnd);
    if (/\{\{c\d+::/.test(selected)) {
      setWarning("That selection already contains a cloze deletion.");
      return;
    }
    setWarning("");
    const next =
      value.slice(0, selectionStart) +
      `{{c${ordinal}::${selected}}}` +
      value.slice(selectionEnd);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const caret = selectionStart + `{{c${ordinal}::${selected}}}`.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const unwrapAll = () => {
    onChange(value.replace(CLOZE_RE, (_m, _n, answer) => answer));
    setWarning("");
  };

  const renumber = () => {
    let n = 0;
    const seen = new Map<string, number>();
    onChange(
      value.replace(CLOZE_RE, (_m, num: string, answer: string, hint?: string) => {
        // Preserve grouping: two spans sharing an ordinal keep sharing one.
        if (!seen.has(num)) seen.set(num, ++n);
        const assigned = seen.get(num)!;
        return `{{c${assigned}::${answer}${hint ? `::${hint}` : ""}}}`;
      }),
    );
    setWarning("");
  };

  const nextOrdinal = ordinals.length ? Math.max(...ordinals) + 1 : 1;

  return (
    <div className="stack" style={{ gap: 8 }}>
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Write the full sentence, then select the part to hide."
        style={{ minHeight: 90 }}
      />

      <div className="row row-wrap" style={{ gap: 6 }}>
        <button type="button" className="btn-sm" onClick={() => wrap(nextOrdinal)}>
          Hide selection as c{nextOrdinal}
        </button>
        {ordinals.map((n) => (
          <button key={n} type="button" className="btn-sm btn-ghost" onClick={() => wrap(n)}>
            + to c{n}
          </button>
        ))}
        <div className="grow" />
        {gaps.length > 0 && (
          <button type="button" className="btn-sm" onClick={renumber}>
            Fix numbering
          </button>
        )}
        {ordinals.length > 0 && (
          <button type="button" className="btn-sm btn-ghost" onClick={unwrapAll}>
            Clear all
          </button>
        )}
      </div>

      {warning && <div className="small" style={{ color: "var(--warn)" }}>{warning}</div>}

      {gaps.length > 0 && (
        <div className="alert alert-warn small">
          <span>⚠</span>
          <div>
            Numbering skips c{gaps.join(", c")}. Anki makes one card per number, so the gap produces
            nothing.
          </div>
        </div>
      )}

      {ordinals.length > 0 ? (
        <div className="stack" style={{ gap: 4 }}>
          <span className="small faint">
            This makes {ordinals.length} card{ordinals.length === 1 ? "" : "s"}:
          </span>
          {ordinals.map((n) => (
            <div key={n} className="small cloze-render" style={{ paddingLeft: 2 }}>
              <span className="tag" style={{ marginRight: 6 }}>c{n}</span>
              <span dangerouslySetInnerHTML={{ __html: renderFor(value, n) }} />
            </div>
          ))}
        </div>
      ) : (
        <div className="small faint">No deletions yet — this card can't be exported.</div>
      )}
    </div>
  );
}

/** Render the card for one ordinal: that span is hidden, the others revealed. */
function renderFor(text: string, ordinal: number): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped.replace(
    /\{\{c(\d+)::(.+?)(?:::(.*?))?\}\}/gs,
    (_m, num: string, answer: string, hint?: string) =>
      Number(num) === ordinal
        ? `<span class="cloze-gap">[${hint ? hint : "..."}]</span>`
        : answer,
  );
}
